import axios, {parseResponseData} from '@/axiosInstance'
import {captureException} from '@sentry/react'
import {z} from 'zod'
import {PaginationResponse} from '@/types'
import {View, ViewEntity} from '../types'

export const HttpGetViewsOptions = z.object({
    params: z.object({
        page: z.number().int().positive(),
        limit: z.number().int().positive(),
        search: z.string().optional(),
        entity: ViewEntity
    })
})
export type HttpGetViewsOptions = z.infer<typeof HttpGetViewsOptions>

export const HttpGetViewsResponse = z.object({
    meta: PaginationResponse,
    data: z.array(View)
})
export type HttpGetViewsResponse = z.infer<typeof HttpGetViewsResponse>

export const httpGetViews = async ({params}: HttpGetViewsOptions) => {
    const response = await axios.get('/views', {params})
    return parseResponseData({
        responseData: response.data,
        responseShape: HttpGetViewsResponse,
        onZodError: captureException
    })
}

export const HttpGetViewOptions = z.object({
    urlParams: z.object({
        id: z.string().min(1)
    })
})
export type HttpGetViewOptions = z.infer<typeof HttpGetViewOptions>

export const httpGetView = async ({urlParams}: HttpGetViewOptions) => {
    const response = await axios.get(`/views/${urlParams.id}`)
    return parseResponseData({
        responseData: response.data,
        responseShape: View,
        onZodError: captureException
    })
}

export const HttpStoreViewOptions = z.object({
    payload: View.omit({id: true}).and(z.object({entity: ViewEntity}))
})
export type HttpStoreViewOptions = z.infer<typeof HttpStoreViewOptions>

export const httpStoreView = async ({payload}: HttpStoreViewOptions) => {
    const response = await axios.post('/views', payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: View,
        onZodError: captureException
    })
}

export const HttpEditViewOptions = z.object({
    urlParams: z.object({
        id: z.string().min(1)
    }),
    payload: View.omit({id: true}).and(z.object({entity: ViewEntity}))
})
export type HttpEditViewOptions = z.infer<typeof HttpEditViewOptions>

export const httpEditView = async ({urlParams, payload}: HttpEditViewOptions) => {
    const response = await axios.patch(`/views/${urlParams.id}`, payload)
    return parseResponseData({
        responseData: response.data,
        responseShape: View,
        onZodError: captureException
    })
}

export const HttpDeleteViewOptions = z.object({
    urlParams: z.object({
        id: z.string().min(1)
    })
})
export type HttpDeleteViewOptions = z.infer<typeof HttpDeleteViewOptions>

export const httpDeleteView = async ({urlParams}: HttpDeleteViewOptions) => {
    await axios.delete(`/views/${urlParams.id}`)
}
